import { useState } from 'react';

import { Card } from '@/components/ui/Card';
import { ClusterStatusPill } from '@/features/mdm/components/ClusterStatusPill';
import type { MdmCluster } from '@/features/mdm/types';

interface Props {
  cluster: MdmCluster;
  busy: boolean;
  onSaveGolden: (id: string, golden: Record<string, unknown>) => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}

function toEditable(golden: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(golden)) {
    out[key] = value === null || value === undefined ? '' : String(value);
  }
  return out;
}

export function ClusterCard({ cluster, busy, onSaveGolden, onApprove, onReject }: Props): JSX.Element {
  const [golden, setGolden] = useState<Record<string, string>>(() => toEditable(cluster.golden_record));
  const isOpen = cluster.status === 'open';
  const fields = Object.keys(golden);

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-900 dark:text-ink-100">
              {cluster.member_count} records look like the same{' '}
              <span className="lowercase">{cluster.entity_type}</span>
            </span>
            <ClusterStatusPill status={cluster.status} />
            {cluster.avg_score !== null && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-white/10 dark:text-ink-300">
                {Math.round(cluster.avg_score * 100)}% similar
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-slate-400 dark:text-ink-400" title={cluster.member_ids.join(', ')}>
            Source rows ({cluster.member_count}): {cluster.member_ids.join(', ')}
          </p>
        </div>
        {isOpen && (
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => onApprove(cluster.id)}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              ✓ Yes, it’s a match
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onReject(cluster.id)}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50 disabled:opacity-50 dark:text-ink-200 dark:ring-white/15 dark:hover:bg-white/5"
            >
              Not a match
            </button>
          </div>
        )}
      </div>

      {/* Combined ("golden") record */}
      <div className="mt-3 rounded-md bg-slate-50 p-3 dark:bg-ink-800 dark:ring-1 dark:ring-inset dark:ring-white/5">
        <p className="text-xs font-medium text-slate-600 dark:text-ink-200">
          Combined record{' '}
          <span className="font-normal text-slate-400 dark:text-ink-400">
            — the single, cleaned-up version we’ll keep{isOpen ? '. Edit any field before confirming.' : '.'}
          </span>
        </p>
        <div className="mt-2 space-y-1.5">
          {fields.map((key) => (
            <div key={key} className="flex items-center gap-2">
              <span className="w-40 shrink-0 truncate text-xs text-slate-500 dark:text-ink-400">{key}</span>
              {isOpen ? (
                <input
                  value={golden[key] ?? ''}
                  onChange={(e) => setGolden((g) => ({ ...g, [key]: e.target.value }))}
                  className="w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-white/10 dark:bg-ink-900 dark:text-ink-100"
                />
              ) : (
                <span className="text-sm text-slate-700 dark:text-ink-200">{golden[key] || '—'}</span>
              )}
            </div>
          ))}
        </div>
        {isOpen && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onSaveGolden(cluster.id, golden)}
            className="mt-2 rounded-md px-3 py-1.5 text-xs font-medium text-brand-600 ring-1 ring-brand-200 hover:bg-brand-50 disabled:opacity-50"
          >
            Save edits
          </button>
        )}
      </div>

      {/* Why these matched */}
      {cluster.pairwise_scores.length > 0 && (
        <details className="mt-3 border-t border-slate-100 pt-2 dark:border-white/10">
          <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-ink-400 dark:hover:text-ink-200">
            Why we grouped these
          </summary>
          <ul className="mt-1 space-y-0.5 text-xs text-slate-500 dark:text-ink-400">
            {cluster.pairwise_scores.map((p, i) => (
              <li key={`${p.a}-${p.b}-${i}`}>
                Rows <span className="font-medium text-slate-600 dark:text-ink-200">{p.a}</span> and{' '}
                <span className="font-medium text-slate-600 dark:text-ink-200">{p.b}</span> are {Math.round(p.score * 100)}%
                similar
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}
